/**
 * CUSTOM: Variant Table
 * File: assets/variant-table.js
 * Features:
 * - Quantity input auto-updates cart (debounced, like native quick-order-list)
 * - Quantities pre-populated from cart on page load
 * - Variant total column updates dynamically
 * - Remove link per row, visible only when qty > 0
 * - Cart count badge updates
 * - Filters, sort, pagination
 * - Unit price = case price / case pack
 * - Sticky total bar
 */

(function () {

  const DEBOUNCE_DELAY = 300;

  // =============================================
  // Debounce
  // =============================================

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  // =============================================
  // Cart helpers
  // =============================================

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  async function updateCartQty(variantId, quantity) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [variantId]: quantity } })
    });
    if (!res.ok) throw new Error(`Cart update failed (${res.status})`);
    return res.json();
  }

  function updateCartCountUI(cart) {
    const count = typeof cart?.item_count === 'number' ? cart.item_count : 0;
    document.querySelectorAll('cart-count, header cart-count').forEach((el) => {
      el.textContent = String(count);
      el.setAttribute('aria-label', `${count} item${count === 1 ? '' : 's'}`);
      el.style.display = count <= 0 ? 'none' : '';
    });
  }

  function formatMoney(amountCents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amountCents / 100);
  }

  function formatMoneyDollars(amountDollars) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amountDollars);
  }

  // =============================================
  // Unit prices
  // =============================================

  function updateUnitPrices(shell) {
    shell.querySelectorAll('tr.variant-row').forEach((row) => {
      const unitEl = row.querySelector('[data-unit-price]');
      if (!unitEl) return;

      const casePack   = Number(row.dataset.casePack || 0);
      const priceCents = Number(row.dataset.price || 0);

      if (!casePack || casePack <= 0 || !priceCents) {
        unitEl.textContent = 'â€”';
        return;
      }

      const unitDollars = priceCents / casePack / 100;
      unitEl.textContent = formatMoneyDollars(Number(unitDollars.toFixed(2)));
    });
  }

  // =============================================
  // Populate inputs + row UI from cart
  // =============================================

  function populateFromCart(shell, cart) {
    const byVariantId = new Map();
    (cart?.items || []).forEach((item) => {
      byVariantId.set(String(item.variant_id), item);
    });

    shell.querySelectorAll('tr.variant-row[data-variant-id]').forEach((row) => {
      const variantId = String(row.dataset.variantId || '');
      const item      = byVariantId.get(variantId);
      const qty       = item ? item.quantity : 0;
      const linePrice = item ? item.original_line_price : 0;

      // Set input value
      const input = row.querySelector('.quantity__input');
      if (input) {
        input.value = String(qty);
        input.dataset.cartQuantity = String(qty);
      }

      // Update variant total
      const totalEl = row.querySelector('[data-variant-total]');
      if (totalEl) totalEl.textContent = formatMoney(linePrice);

      // Show/hide remove link
      updateRemoveLink(row, qty);
    });
  }

  function updateRemoveLink(row, qty) {
    const removeLink = row.querySelector('[data-remove]');
    if (!removeLink) return;
    if (qty > 0) {
      removeLink.classList.remove('invisible');
    } else {
      removeLink.classList.add('invisible');
    }
  }

  // =============================================
  // Total bar
  // =============================================

  function updateTotalBar(shell, cart) {
    const bar = shell.querySelector('[data-vt-bar]');
    if (!bar) return;

    const productVariantIds = new Set(
      Array.from(shell.querySelectorAll('tr.variant-row[data-variant-id]'))
        .map((r) => String(r.dataset.variantId))
    );

    let totalQty   = 0;
    let totalPrice = 0;

    (cart?.items || []).forEach((item) => {
      if (productVariantIds.has(String(item.variant_id))) {
        totalQty   += item.quantity;
        totalPrice += item.original_line_price;
      }
    });

    const qtyEl   = bar.querySelector('[data-vt-total-qty]');
    const priceEl = bar.querySelector('[data-vt-total-price]');

    if (qtyEl)   qtyEl.textContent   = String(totalQty);
    if (priceEl) priceEl.textContent = formatMoney(totalPrice);
  }

  // =============================================
  // Loading state per row
  // =============================================

  function setRowLoading(row, loading) {
    const input = row.querySelector('.quantity__input');
    if (input) input.disabled = loading;
  }

  // =============================================
  // Handle quantity change
  // =============================================

  function handleQuantityChange(shell, input) {
    const row       = input.closest('tr.variant-row');
    if (!row) return;

    const variantId = input.dataset.variantId;
    const qty       = Math.max(0, parseInt(input.value, 10) || 0);

    // Snap input to valid value
    input.value = String(qty);

    setRowLoading(row, true);

    updateCartQty(variantId, qty)
      .then((cart) => {
        // Update this row's variant total immediately
        const item      = (cart.items || []).find((i) => String(i.variant_id) === variantId);
        const linePrice = item ? item.original_line_price : 0;

        const totalEl = row.querySelector('[data-variant-total]');
        if (totalEl) totalEl.textContent = formatMoney(linePrice);

        input.dataset.cartQuantity = String(qty);
        updateRemoveLink(row, qty);

        // Update total bar + cart count
        updateTotalBar(shell, cart);
        updateCartCountUI(cart);

        // Update all shells (in case multiple on page)
        document.querySelectorAll('[data-variant-table]').forEach((s) => {
          if (s !== shell) populateFromCart(s, cart);
        });
      })
      .catch((err) => {
        console.error('Cart update failed:', err);
        // Revert input to last known cart quantity
        input.value = input.dataset.cartQuantity || '0';
      })
      .finally(() => {
        setRowLoading(row, false);
      });
  }

  // =============================================
  // Remove all
  // =============================================

  function initRemoveAll(shell) {
    const btn = shell.querySelector('[data-vt-remove-all]');
    if (!btn) return;

    btn.addEventListener('click', async () => {
      const confirmed = window.confirm('Remove all items from cart?');
      if (!confirmed) return;

      const variantIds = Array.from(
        shell.querySelectorAll('tr.variant-row[data-variant-id]')
      ).map((r) => r.dataset.variantId);

      const updates = {};
      variantIds.forEach((id) => { updates[id] = 0; });

      try {
        const res  = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        const cart = await res.json();

        updateCartCountUI(cart);
        populateFromCart(shell, cart);
        updateTotalBar(shell, cart);
      } catch (err) {
        console.error('Remove all failed:', err);
      }
    });
  }

  // =============================================
  // Filter / Sort / Pagination
  // =============================================

  function initVariantTable(shell) {
    const tbody = shell.querySelector('[data-variant-tbody]');
    if (!tbody) return;

    const rows = Array.from(tbody.querySelectorAll('tr.variant-row'));

    const filterEls  = Array.from(shell.querySelectorAll('.vt-filter'));
    const sortEl     = shell.querySelector('[data-sort]');
    const pageSizeEl = shell.querySelector('[data-pagesize]');
    const clearBtn   = shell.querySelector('[data-clear-filters]');

    const resultsEl  = shell.querySelector('[data-results]');
    const pageInfoEl = shell.querySelector('[data-page-info]');
    const prevBtn    = shell.querySelector('[data-page="prev"]');
    const nextBtn    = shell.querySelector('[data-page="next"]');

    let page     = 1;
    let pageSize = parseInt(shell.dataset.pageSize || '5', 10);
    let sortMode = 'default';
    let filters  = { option1: '', option2: '', option3: '' };

    function matchesFilters(row) {
      if (filters.option1 && (row.dataset.option1 || '') !== filters.option1) return false;
      if (filters.option2 && (row.dataset.option2 || '') !== filters.option2) return false;
      if (filters.option3 && (row.dataset.option3 || '') !== filters.option3) return false;
      return true;
    }

    function getSorted(list) {
      if (sortMode === 'price-asc') {
        return list.slice().sort((a, b) =>
          (parseInt(a.dataset.price, 10) || 0) - (parseInt(b.dataset.price, 10) || 0)
        );
      }
      if (sortMode === 'price-desc') {
        return list.slice().sort((a, b) =>
          (parseInt(b.dataset.price, 10) || 0) - (parseInt(a.dataset.price, 10) || 0)
        );
      }
      return list;
    }

    function render() {
      const filtered   = rows.filter(matchesFilters);
      const sorted     = getSorted(filtered);
      const total      = sorted.length;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));

      page = Math.min(page, totalPages);

      const start    = (page - 1) * pageSize;
      const end      = start + pageSize;
      const pageRows = new Set(sorted.slice(start, end));

      rows.forEach((r) => { r.hidden = !pageRows.has(r); });

      const shownFrom = total === 0 ? 0 : start + 1;
      const shownTo   = Math.min(end, total);

      if (resultsEl)  resultsEl.textContent = `Showing ${shownFrom}-${shownTo} of ${total}`;
      if (pageInfoEl) pageInfoEl.textContent = `Page ${page} of ${totalPages}`;
      if (prevBtn)    prevBtn.disabled = page <= 1;
      if (nextBtn)    nextBtn.disabled = page >= totalPages;

      updateUnitPrices(shell);
    }

    // Quantity input â€” debounced
    const debouncedChange = debounce((input) => {
      handleQuantityChange(shell, input);
    }, DEBOUNCE_DELAY);

    shell.querySelectorAll('.quantity__input').forEach((input) => {
      input.addEventListener('change', () => debouncedChange(input));
    });

    // Remove link per row
    shell.querySelectorAll('[data-remove]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = link.dataset.remove;
        const row       = link.closest('tr.variant-row');
        const input     = row?.querySelector('.quantity__input');
        if (input) {
          input.value = '0';
          handleQuantityChange(shell, input);
        }
      });
    });

    // Filters
    filterEls.forEach((sel) => {
      sel.addEventListener('change', () => {
        filters[sel.dataset.filter] = sel.value;
        page = 1;
        render();
      });
    });

    // Sort
    if (sortEl) {
      sortEl.addEventListener('change', () => {
        sortMode = sortEl.value;
        page = 1;
        render();
      });
    }

    // Page size
    if (pageSizeEl) {
      pageSizeEl.value = String(pageSize);
      pageSizeEl.addEventListener('change', () => {
        pageSize = Math.max(1, parseInt(pageSizeEl.value, 10) || 5);
        page = 1;
        render();
      });
    }

    // Clear
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        filters = { option1: '', option2: '', option3: '' };
        filterEls.forEach((sel) => (sel.value = ''));
        if (sortEl) sortEl.value = 'default';
        sortMode = 'default';
        page = 1;
        render();
      });
    }

    // Pagination
    if (prevBtn) prevBtn.addEventListener('click', () => { page = Math.max(1, page - 1); render(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { page = page + 1; render(); });

    render();
  }

  // =============================================
  // Init on DOM ready
  // =============================================

  document.addEventListener('DOMContentLoaded', async () => {
    let cart = null;

    try {
      cart = await fetchCart();
      updateCartCountUI(cart);
    } catch (err) {
      console.error('Could not fetch cart on load:', err);
    }

    document.querySelectorAll('[data-variant-table]').forEach((shell) => {
      initVariantTable(shell);
      initRemoveAll(shell);
      updateUnitPrices(shell);
      if (cart) {
        populateFromCart(shell, cart);
        updateTotalBar(shell, cart);
      }
    });
  });

})();